if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        this.buyNowButton = this.querySelector('[data-buy-now="true"]');

        if (this.buyNowButton) {
          this.buyNowButton.addEventListener('click', this.onBuyNowHandler.bind(this));
          // チェックアウトへ遷移した後にブラウザバックで戻ると、bfcache が
          // ローディング中のボタン状態ごとページを復元するため、pageshow で
          // 必ず元に戻す(通常表示の再訪でも無害)。
          window.addEventListener('pageshow', this.resetBuyNowButton.bind(this));
        }

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      // Storefront API cartCreate(2026-07)で既存カートと独立したチェックアウトを
      // 開始する。既存カートには一切触れず、失敗時も旧方式(/cart/add.js →
      // /checkout)へはフォールバックしない。
      async onBuyNowHandler(evt) {
        evt.preventDefault();

        if (!this.buyNowButton || this.buyNowButton.disabled) return;

        this.handleErrorMessage();

        this.buyNowButton.disabled = true;
        this.buyNowButton.classList.add('loading');

        const spinner = this.buyNowButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        try {
          if (typeof window.JuwariCartCreateCheckout === 'undefined') {
            throw new Error('購入処理を読み込めませんでした。ページを再読み込みしてください。');
          }

          const formData = new FormData(this.form);

          const variantId = formData.get('id');
          if (!variantId) {
            throw new Error('商品情報を取得できませんでした。ページを再読み込みしてください。');
          }

          const quantity = parseInt(formData.get('quantity'), 10) || 1;
          const sellingPlanId = (formData.get('selling_plan') || '').toString().trim();

          // Mikawaya V3 の購入方法ラジオで定期便が選択されているのに selling_plan が
          // 取得できない場合は、単品として処理せず必ず停止する。
          const purchaseOption = document.querySelector('input[name="purchase_option"]:checked');
          if (purchaseOption && purchaseOption.value !== 'one-time' && sellingPlanId === '') {
            throw new Error(
              '定期便プランの情報を取得できませんでした。お手数ですがページを再読み込みし、プランを選択し直してください。'
            );
          }

          const attributes = [];
          for (const [key, value] of formData.entries()) {
            const propertyName = key.match(/^properties\[(.+)\]$/);
            if (propertyName && String(value).trim() !== '') {
              attributes.push({ key: propertyName[1], value: String(value) });
            }
          }

          const checkoutUrl = await window.JuwariCartCreateCheckout.createCheckout({
            variantId: variantId,
            quantity: quantity,
            sellingPlanId: sellingPlanId === '' ? null : sellingPlanId,
            attributes: attributes,
          });

          window.location.assign(checkoutUrl);
        } catch (error) {
          console.error(error);

          this.handleErrorMessage(
            error.message || '処理中にエラーが発生しました。'
          );

          this.resetBuyNowButton();
        }
      }

      // 「今すぐ購入」をローディング前の状態に戻す(エラー時・bfcache 復元時の共通処理)。
      // 売り切れ等で最初から disabled のボタンを誤って有効化しないよう、
      // loading 中の場合のみ処理する。ボタン文言は変更していないため復元不要。
      resetBuyNowButton() {
        if (!this.buyNowButton || !this.buyNowButton.classList.contains('loading')) return;

        this.buyNowButton.disabled = false;
        this.buyNowButton.removeAttribute('aria-disabled');
        this.buyNowButton.classList.remove('loading');

        const spinner = this.buyNowButton.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.submitButton.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.submitButton.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}